import { Fragment, useCallback, useEffect, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Download,
  Eye,
  Filter,
  Plus,
  QrCode,
  Search,
  Trash2,
  Upload,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Edit,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dropdown } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { PageHeader } from '@/components/ui/page-header'
import { BarcodeScanner } from '@/components/ui/barcode-scanner'
import { DeviceForm } from '@/components/forms/DeviceForm'
import { DeviceModelForm } from '@/components/forms/DeviceModelForm'
import { AssignmentModal } from '@/components/modals/AssignmentModal'
import { ImportCsvModal } from '@/components/modals/ImportCsvModal'
import { apiClient } from '@/lib/api-client'
import { normalizeBarcode } from '@/lib/barcode'
import {
  canAssign,
  canReturn,
  getActiveAssignment,
  returnAsset,
  ownershipLabel,
  powerDetail,
  statusLabel,
  typeLabel,
} from '@/lib/device-format'
import { useAuth } from '@/hooks/useAuth'
import { getStatusColor } from '@shared/utils'
import { toast } from 'react-hot-toast'

export function DevicesPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchQuery = searchParams.get('search') ?? ''
  const statusFilter = searchParams.get('status') ?? 'all'
  const ownershipFilter = searchParams.get('ownership') ?? 'all'
  const projectFilter = searchParams.get('project') ?? ''
  const currentPage = Math.max(1, Number(searchParams.get('page') || '1') || 1)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [selectedDevice, setSelectedDevice] = useState<any>(null)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [assignTarget, setAssignTarget] = useState<{ id: number; label: string; assignment?: any } | null>(null)
  const [editModelId, setEditModelId] = useState<number | null>(null)
  const [returningId, setReturningId] = useState<number | null>(null)

  const updateFilters = useCallback((patch: Record<string, string | number | undefined>) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous)
      Object.entries(patch).forEach(([key, value]) => {
        if (value === undefined || value === '' || value === 'all' || value === 1) next.delete(key)
        else next.set(key, String(value))
      })
      return next
    }, { replace: true })
  }, [setSearchParams])

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => (await apiClient.get('/projects')).data,
    enabled: filtersOpen || ownershipFilter === 'project',
  })

  const { data: devicesData, isLoading } = useQuery({
    queryKey: ['devices', searchQuery, statusFilter, ownershipFilter, projectFilter, currentPage],
    queryFn: async () => {
      const params: any = { page: currentPage, limit: 12, grouped: true }
      if (searchQuery.trim()) params.search = searchQuery.trim()
      if (statusFilter !== 'all') params.status = statusFilter
      if (ownershipFilter !== 'all') params.ownershipType = ownershipFilter
      if (ownershipFilter === 'project' && projectFilter) params.projectId = projectFilter
      const response = await apiClient.get('/devices', { params })
      return response.data
    },
  })

  const groups = devicesData?.groups || []
  const pagination = devicesData?.pagination
  const activeFilterCount = [
    statusFilter !== 'all',
    ownershipFilter !== 'all',
    ownershipFilter === 'project' && !!projectFilter,
  ].filter(Boolean).length

  useEffect(() => {
    if (!filtersOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFiltersOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [filtersOpen])

  const isExpanded = (group: { id: string; assets?: { status?: string }[] }) => {
    if (expanded[group.id] !== undefined) return expanded[group.id]
    if (searchQuery.trim()) return true
    return (group.assets || []).some((asset) => asset.status === 'assigned_pending')
  }

  const deleteDeviceMutation = useMutation({
    mutationFn: async (deviceId: number) => apiClient.delete(`/devices/${deviceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      queryClient.invalidateQueries({ queryKey: ['device-models'] })
      toast.success('Asset deleted')
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Delete failed'),
  })

  const handleBarcodeScan = useCallback(async (result: string) => {
    const code = normalizeBarcode(result)
    if (!code) return
    setShowBarcodeScanner(false)
    updateFilters({ search: code, page: 1 })
    try {
      const response = await apiClient.get('/devices/lookup', { params: { code } })
      toast.success(`Found ${response.data.name}`)
      navigate(`/devices/${response.data.id}`)
    } catch (error: any) {
      if (error.response?.status === 404) {
        toast.error('No device matches this QR code')
        return
      }
      toast.error(error.response?.data?.message || 'Lookup failed')
    }
  }, [navigate, updateFilters])

  const handleExportCSV = async () => {
    try {
      const params: any = {}
      if (searchQuery.trim()) params.search = searchQuery.trim()
      if (statusFilter !== 'all') params.status = statusFilter
      if (ownershipFilter !== 'all') params.ownershipType = ownershipFilter
      if (ownershipFilter === 'project' && projectFilter) params.projectId = projectFilter
      const response = await apiClient.get('/devices/export/csv', { params, responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }))
      const link = document.createElement('a')
      link.href = url
      link.download = `devices-export-${new Date().toISOString().split('T')[0]}.csv`
      link.click()
      window.URL.revokeObjectURL(url)
      toast.success('CSV exported')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Export failed')
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Devices"
        actions={
          user?.role === 'Admin' ? (
            <>
              <Button variant="outline" onClick={() => setIsImportOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Import
              </Button>
              <Button variant="outline" onClick={handleExportCSV}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
              <Button onClick={() => { setSelectedDevice(null); setIsFormOpen(true) }}>
                <Plus className="mr-2 h-4 w-4" />
                Add assets
              </Button>
            </>
          ) : null
        }
      />

      <div className="data-panel">
        <div className="flex flex-col gap-3 border-b border-border px-3 py-3 lg:flex-row lg:items-center">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(event) => updateFilters({ search: event.target.value, page: 1 })}
              placeholder="Search model, serial, or QR code"
              className="h-11 pl-9 lg:h-9"
            />
          </div>
          <div className="grid w-full grid-cols-2 gap-2 lg:flex lg:w-auto lg:flex-wrap lg:items-center">
            <Button variant="outline" size="sm" className="h-11 lg:h-9" onClick={() => setFiltersOpen(true)}>
              <Filter className="mr-2 h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-2 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground">
                  {activeFilterCount}
                </span>
              )}
            </Button>
            <Button variant="outline" size="sm" className="h-11 lg:h-9" onClick={() => setShowBarcodeScanner(true)}>
              <QrCode className="mr-2 h-4 w-4" />
              Scan
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : groups.length === 0 ? (
          <div className="px-4 py-16 text-center">
            <p className="text-sm font-medium">No assets found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Power</th>
                  <th>Ownership</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group: any) => {
                  const open = isExpanded(group)
                  const assets = group.assets || []
                  return (
                    <Fragment key={group.id}>
                      <tr className="cursor-pointer" onClick={() => setExpanded((current) => ({
                        ...current,
                        [group.id]: !isExpanded(group),
                      }))}>
                        <td>
                          <div className="flex items-center gap-2">
                            <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? '' : '-rotate-90'}`} />
                            <div className="min-w-0">
                              <p className="font-medium">{group.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {[group.brand, group.modelNumber, `${assets.length} asset${assets.length === 1 ? '' : 's'}`]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td data-label="Power">
                          <p>{typeLabel(group)}</p>
                          <p className="text-xs text-muted-foreground">
                            {group.deviceType === 'chargeable'
                              ? `${assets.filter((asset: any) => asset.chargerAvailable).length} of ${assets.length} with charger`
                              : powerDetail(assets[0] || group)}
                          </p>
                        </td>
                        <td data-label="Ownership">{ownershipSummary(assets)}</td>
                        <td data-label="Status" className="text-xs text-muted-foreground">{statusSummary(assets)}</td>
                        <td className="is-actions" onClick={(event) => event.stopPropagation()}>
                          {group.modelId && (
                            <div className="flex justify-end gap-1">
                              {user?.role === 'Admin' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8"
                                  onClick={() => setEditModelId(group.modelId)}
                                >
                                  <Edit className="mr-1.5 h-3.5 w-3.5" />
                                  Edit model
                                </Button>
                              )}
                              <Button asChild variant="ghost" size="sm" className="h-8">
                                <Link to={`/devices/models/${group.modelId}`}>
                                  Open model
                                </Link>
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {open && assets.map((device: any) => (
                        <tr key={device.id} className="is-child bg-muted/30">
                          <td>
                            <div className="flex items-center gap-2 pl-6">
                              <span className="h-px w-3 bg-border" />
                              <Link
                                to={`/devices/${device.id}`}
                                className="font-mono text-xs font-medium text-primary hover:underline"
                              >
                                {device.serialNumber || device.name}
                              </Link>
                            </div>
                          </td>
                          <td data-label="Power" className="text-muted-foreground">{powerDetail(device)}</td>
                          <td data-label="Ownership">{ownershipLabel(device)}</td>
                          <td data-label="Status">
                            <Badge className={getStatusColor(device.status)}>
                              {statusLabel(device.status)}
                            </Badge>
                          </td>
                          <td className="is-actions" onClick={(event) => event.stopPropagation()}>
                            <div className="flex justify-end gap-1">
                              {user?.role === 'Admin' && canReturn(device.status) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8"
                                  disabled={returningId === device.id}
                                  onClick={() => markReturned(device, async () => {
                                    queryClient.invalidateQueries({ queryKey: ['devices'] })
                                    queryClient.invalidateQueries({ queryKey: ['assignments'] })
                                    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
                                  }, setReturningId)}
                                >
                                  Return
                                </Button>
                              )}
                              {user?.role === 'Admin' && canAssign(device.status) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8"
                                  onClick={() => openAssetAssignment(device, setAssignTarget)}
                                >
                                  {device.status === 'available' ? 'Assign' : 'Reassign'}
                                </Button>
                              )}
                              <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                                <Link to={`/devices/${device.id}`} aria-label="View asset">
                                  <Eye className="h-4 w-4" />
                                </Link>
                              </Button>
                              {user?.role === 'Admin' && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    aria-label="Edit asset"
                                    onClick={() => {
                                      setSelectedDevice(device)
                                      setIsFormOpen(true)
                                    }}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive"
                                    aria-label="Delete asset"
                                    onClick={() => {
                                      if (window.confirm('Delete this asset?')) deleteDeviceMutation.mutate(device.id)
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {pagination && (
          <div className="flex items-center justify-between border-t border-border px-3 py-2 text-sm text-muted-foreground">
            <span>
              {pagination.totalItems} models · {pagination.totalAssets ?? 0} assets · page {pagination.currentPage} of {pagination.totalPages || 1}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                disabled={!pagination.hasPrevPage}
                onClick={() => updateFilters({ page: pagination.currentPage - 1 })}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                disabled={!pagination.hasNextPage}
                onClick={() => updateFilters({ page: pagination.currentPage + 1 })}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={() => setFiltersOpen(false)} />
      )}
      <aside
        className={`fixed z-50 flex w-full flex-col border-border bg-card shadow-xl transition-transform duration-200 max-lg:inset-x-0 max-lg:bottom-0 max-lg:max-h-[85dvh] max-lg:rounded-t-3xl max-lg:border-t lg:inset-y-0 lg:right-0 lg:max-w-sm lg:border-l ${
          filtersOpen ? 'translate-y-0 lg:translate-x-0' : 'pointer-events-none translate-y-full lg:translate-x-full lg:translate-y-0'
        }`}
        aria-hidden={!filtersOpen}
      >
        <div className="flex h-14 items-center justify-between border-b border-border px-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-semibold">Filters</p>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setFiltersOpen(false)} aria-label="Close filters">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
          <FilterField label="Ownership">
            <Dropdown
              value={ownershipFilter}
              onChange={(value) => updateFilters({
                ownership: value,
                project: value === 'project' ? projectFilter : undefined,
                page: 1,
              })}
              options={[
                { value: 'all', label: 'All ownership' },
                { value: 'company', label: 'Company' },
                { value: 'project', label: 'Project' },
              ]}
            />
          </FilterField>
          {ownershipFilter === 'project' && (
            <FilterField label="Project">
              <Dropdown
                value={projectFilter}
                placeholder="All projects"
                onChange={(value) => updateFilters({ project: value, page: 1 })}
                options={[
                  { value: '', label: 'All projects' },
                  ...projects.map((project: { id: number; name: string }) => ({
                    value: String(project.id),
                    label: project.name,
                  })),
                ]}
              />
            </FilterField>
          )}
          <FilterField label="Status">
            <Dropdown
              value={statusFilter}
              onChange={(value) => updateFilters({ status: value, page: 1 })}
              options={[
                { value: 'all', label: 'All status' },
                { value: 'available', label: 'Available' },
                { value: 'unavailable', label: 'Unavailable' },
                { value: 'broken', label: 'Broken' },
                { value: 'completely_used', label: 'Completely used' },
                { value: 'eol', label: 'EoL' },
                { value: 'assigned_pending', label: 'Pending approval' },
                { value: 'assigned', label: 'Assigned' },
              ]}
            />
          </FilterField>
        </div>
        <div className="border-t border-border p-4">
          <Button
            variant="outline"
            className="w-full"
            disabled={activeFilterCount === 0}
            onClick={() => updateFilters({ status: 'all', ownership: 'all', project: undefined, page: 1 })}
          >
            Clear filters
          </Button>
        </div>
      </aside>
      <AssignmentModal
        isOpen={!!assignTarget}
        onClose={() => setAssignTarget(null)}
        deviceId={assignTarget?.id || 0}
        deviceName={assignTarget?.label || ''}
        currentAssignment={assignTarget?.assignment}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['devices'] })
          queryClient.invalidateQueries({ queryKey: ['assignments'] })
        }}
      />
      <BarcodeScanner
        isOpen={showBarcodeScanner}
        onClose={() => setShowBarcodeScanner(false)}
        onScan={handleBarcodeScan}
      />
      <DeviceModelForm
        isOpen={!!editModelId}
        modelId={editModelId}
        onClose={() => setEditModelId(null)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['devices'] })
          queryClient.invalidateQueries({ queryKey: ['device-models'] })
          queryClient.invalidateQueries({ queryKey: ['device-model'] })
        }}
      />
      <DeviceForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          setSelectedDevice(null)
        }}
        device={selectedDevice}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['devices'] })
          queryClient.invalidateQueries({ queryKey: ['device-models'] })
        }}
      />
      <ImportCsvModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        title="Import devices"
        templateUrl="/devices/import/template"
        importUrl="/devices/import/csv"
        hint="Each row is one asset. Repeat the model name, or leave it blank, to add another serial number under the same model. Replace the sample rows. Nothing is saved unless every row is valid."
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['devices'] })
          queryClient.invalidateQueries({ queryKey: ['device-models'] })
        }}
      />
    </div>
  )
}

async function markReturned(
  asset: { id: number; serialNumber?: string; name?: string },
  onDone: () => void,
  setReturningId: (id: number | null) => void,
) {
  const label = asset.serialNumber || asset.name || 'this asset'
  if (!window.confirm(`Mark ${label} as returned? It will be available to assign again.`)) return
  setReturningId(asset.id)
  try {
    await returnAsset(asset.id)
    toast.success('Asset marked returned')
    onDone()
  } catch (error: any) {
    toast.error(error.response?.data?.message || error.message || 'Could not mark the asset returned')
  } finally {
    setReturningId(null)
  }
}

async function openAssetAssignment(
  asset: any,
  setAssignTarget: (target: { id: number; label: string; assignment?: any } | null) => void,
) {
  const assigned = asset.status === 'assigned' || asset.status === 'assigned_pending'
  const assignment = assigned ? await getActiveAssignment(asset.id) : null
  if (assigned && !assignment) {
    toast.error('No active assignment was found for this asset')
    return
  }
  setAssignTarget({
    id: asset.id,
    label: asset.serialNumber || asset.name,
    assignment,
  })
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium">{label}</label>
      {children}
    </div>
  )
}

function ownershipSummary(assets: any[]) {
  const kinds = new Set(assets.map((asset) => asset.ownershipType || 'company'))
  if (kinds.size > 1) return 'Mixed'
  return kinds.has('project') ? 'Project' : 'Company'
}

function statusSummary(assets: any[]) {
  const counts = new Map<string, number>()
  assets.forEach((asset) => {
    const label = statusLabel(asset.status).toLowerCase()
    counts.set(label, (counts.get(label) || 0) + 1)
  })
  return Array.from(counts.entries()).map(([label, count]) => `${count} ${label}`).join(', ')
}
